/**
 * viewService.ts
 * Detects the current Office presentation view (edit vs. slideshow/read)
 * and provides a polling-based subscription for view change events.
 *
 * Note: Office.js does not provide a reliable "view changed" event for
 * PowerPoint in Webview2. We use a 500ms polling strategy as the most
 * robust cross-version approach.
 */

import type { OfficeViewType } from '@types-local/office.types';

// ---- Single read -------------------------------------------

/**
 * Returns the current active view type.
 * Returns 'edit' on any error (safe default to avoid false overlays).
 */
export function getCurrentView(): Promise<OfficeViewType> {
  return new Promise((resolve) => {
    Office.context.document.getActiveViewAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        resolve('edit');
        return;
      }
      // Office returns 'read' for Slide Show mode, 'edit' for Normal/Slide Sorter
      const view = result.value as string;
      resolve(view === 'read' ? 'read' : 'edit');
    });
  });
}

// ---- Polling subscription ----------------------------------

type ViewChangeCallback = (view: OfficeViewType) => void;

let _pollingInterval: ReturnType<typeof setInterval> | null = null;
let _lastView: OfficeViewType | null = null;
const _listeners = new Set<ViewChangeCallback>();

/**
 * Starts the view-polling loop (500ms interval).
 * Safe to call multiple times — only starts one loop.
 */
function ensurePolling(): void {
  if (_pollingInterval !== null) return;

  _pollingInterval = setInterval(async () => {
    const currentView = await getCurrentView();
    if (currentView !== _lastView) {
      _lastView = currentView;
      _listeners.forEach((cb) => cb(currentView));
    }
  }, 500);
}

/**
 * Registers a callback that fires whenever the Office view changes.
 * Returns an unsubscribe function — call it inside useEffect cleanup.
 */
export function onViewChange(callback: ViewChangeCallback): () => void {
  _listeners.add(callback);
  ensurePolling();

  return () => {
    _listeners.delete(callback);
    // Stop polling when no one is listening
    if (_listeners.size === 0 && _pollingInterval !== null) {
      clearInterval(_pollingInterval);
      _pollingInterval = null;
      _lastView = null;
    }
  };
}
